import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'gty.sidebar.collapsed';

/** Tailwind's `md`. Below this the sidebar is a drawer, not a rail. */
const MOBILE_QUERY = '(max-width: 767px)';

function readStoredCollapsed(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    // Private mode / disabled storage. A layout preference is not worth
    // breaking the app over — fall back to expanded.
    return false;
  }
}

export interface SidebarState {
  /** Rail mode on desktop. Meaningless on mobile, where the drawer is used. */
  collapsed: boolean;
  toggleCollapsed: () => void;
  isMobile: boolean;
  drawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
}

export function useSidebarState(): SidebarState {
  const [collapsed, setCollapsed] = useState(readStoredCollapsed);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (event: MediaQueryListEvent): void => {
      setIsMobile(event.matches);
      // Leaving mobile with the drawer open would otherwise strand an
      // off-canvas panel and its scrim over the desktop layout.
      if (!event.matches) setDrawerOpen(false);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // Preference simply does not persist. Not worth surfacing.
      }
      return next;
    });
  }, []);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  return { collapsed, toggleCollapsed, isMobile, drawerOpen, openDrawer, closeDrawer };
}
